
import * as THREE from 'three';
import store from '../redux/store';
import {
  selected_object,
  setPushIntersectedObject,
  removeIntersectedObject,
} from '../redux/Slice/gridSlice';
import { selectObjectInScene } from '../pages/Dashboard/GridPage';
import {
  clearOutlineProxyForPoints,
  refreshParticleSelectionOutline,
} from './ThreeJsUtils/selectionOutline';
import {
  startAnimation,
  stopAnimation,
  pauseAnimation,
} from './particles/animationControls';
import { setLifeTimeout, clearLifeTimeout } from './particles/lifeUtils';
import handleTerrain from './particles/handleTerrain';
import handleWater from './particles/handleWater';
import handleClouds from './particles/handleClouds';
import handleStars from './particles/handleStars';
import handleRain from './particles/handleRain';
import handleSnow from './particles/handleSnow';
import { ensurePointsMaterialOrthoSupport, resyncSceneParticlePoints } from './particles/syncParticlePointSizesForCamera';
import handleFoliage from './particles/handleFoliage';
import {
  createParticleTexture,
  applyParticleShapeMap,
  getDefaultParticleShape,
} from './particles/particleShapeTextures';
import { hasValidParticleCount } from './ThreeJsUtils/assetVisibilityUtils';


// Helper: create a BufferGeometry suitable for a fire particle system
// createFireGeometry -- now accepts an optional speedMultiplier to control motion speed
const createFireGeometry = (
  range = 0.5,
  fireHeight = 1.5,
  coreCount = 100,
  speedMultiplier = 1.0
) => {
  const positions = new Float32Array(coreCount * 3);
  const colors = new Float32Array(coreCount * 3);
  const sizes = new Float32Array(coreCount);
  // per-particle velocity (speed) and timing arrays
  const velocities = new Float32Array(coreCount * 3);
  const life = new Float32Array(coreCount); // used as particle 'age' for swirl/flicker
  const lifespans = new Float32Array(coreCount);

  const config = { range, speedMultiplier };
  for (let i = 0; i < coreCount; i++) {
    resetFireParticle(
      i,
      positions,
      velocities,
      colors,
      sizes,
      life,
      lifespans,
      config
    );
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

  // Store auxiliary arrays in userData for runtime updates/respawns
  geometry.userData = { velocities, life, lifespans, config };

  return geometry;
};

// Helper: create a Points-like material compatible with the small API expected
// by existing code (setPerspective, update) while using createParticleTexture
// Lower default size so particles are visually smaller (can still be overridden)
const createFireMaterial = ({
  color = '#ffb600',
  size = 0.12,
  opacity = 0.9,
  sizeAttenuation = true,
  blending = 'NormalBlending',
  shape = 'fire',
  shapeRotation = 0,
  shapeScale = 1,
} = {}) => {
  const shapeKey = String(shape || 'fire').toLowerCase();
  const textureSize = shapeKey === 'fire' ? 128 : 64;
  // Use createParticleTexture to get a map
  const map = createParticleTexture(
    shape,
    color,
    textureSize,
    shapeRotation,
    shapeScale
  );

  let blendingMode = THREE.NormalBlending;
  if (blending === 'AdditiveBlending') blendingMode = THREE.AdditiveBlending;
  else if (blending === 'MultiplyBlending') blendingMode = THREE.MultiplyBlending;

  const pointsMaterial = new THREE.PointsMaterial({
    size: size,
    map,
    // Keep near-white so the fire PNG colors stay visible; UI color still tints lightly
    color:
      shapeKey === 'fire'
        ? new THREE.Color('#ffffff')
        : new THREE.Color(color),
    transparent: true,
    opacity: opacity,
    depthWrite: false,
    alphaTest: 0.01,
    sizeAttenuation: sizeAttenuation,
    vertexColors: shapeKey !== 'fire',
    blending: shapeKey === 'fire' ? THREE.AdditiveBlending : blendingMode,
  });

  // Extend material with small compatibility methods used in this file
  pointsMaterial.setPerspective = function (fov, height) {
    // Store perspective data for potential size scaling by update
    this.userData = this.userData || {};
    this.userData.fov = fov;
    this.userData.height = height;
  };

  // store a stable base size so update() uses a constant reference
  pointsMaterial.userData = pointsMaterial.userData || {};
  pointsMaterial.userData.baseSize = size;
  pointsMaterial.userData.authoredSize = size;
  pointsMaterial.userData.shape = shape;
  pointsMaterial.userData.shapeRotation = shapeRotation;
  pointsMaterial.userData.shapeScale = shapeScale;
  ensurePointsMaterialOrthoSupport(pointsMaterial);

  pointsMaterial.update = function (speedScale = 1.0) {
    // Slight procedural animation: pulse the size a bit based on time
    const t = Date.now() * 0.001;
    // apply a subtle pulse to size without drastically changing appearance
    this.size = Math.max(
      0.0001,
      (this.userData.baseSize || size) *
        (this.userData.shapeScale || 1) *
        (1.0 + 0.005 * Math.sin(t * 6.0 * speedScale))
    );
    // ensure renderer picks up size changes without full shader recompile
  };

  return pointsMaterial;
};

const handleFire = (scene, particle) => {
  if (!scene || typeof camera === 'undefined') return;
  

  // State variables from particle parameter
  const rawCore = particle?.coreCount;
  const coreCount =
    rawCore === 0 || rawCore === '0' || rawCore === ''
      ? 0
      : (parseInt(rawCore) || 100);
  const emberCount = particle?.emberCount || 150;
  const color = particle.color || '#ffb600';
  const opacity = particle?.opacity || 0.9;
  const size = particle?.size || 0.12;
  const range = particle?.range || 0.5;
  const turbulence = particle?.turbulence || 0.1;
  const life = particle?.life || 1.0;
  const sizeVariation = particle?.sizeVariation || 0.3;
  const velocity = particle?.velocity || { x: 0, y: 0, z: 0 };
  const wind = particle?.wind || { x: 0, y: 0, z: 0 };
  const heatIntensity = particle?.heatIntensity || 0.1;
  const blending = particle?.blending || 'NormalBlending';
  const sizeAttenuation =
    particle?.sizeAttenuation !== undefined ? particle.sizeAttenuation : true;
  const shape = particle?.shape ?? getDefaultParticleShape('fire');
  const shapeRotation = particle?.shapeRotation || 0;
  const shapeScale = particle?.shapeScale || 1.0;

  const fireHeight = range * 3;
  // speedMultiplier controls how fast particles move & age. Increase default for faster animation
  const speedMultiplier = particle?.speedMultiplier ?? 2.5;

  // Create fire geometry & material using local helpers
  const geometry = createFireGeometry(
    range,
    fireHeight,
    coreCount,
    speedMultiplier
  );
  const material = createFireMaterial({
    color,
    size,
    opacity,
    sizeAttenuation,
    blending,
    shape,
    shapeRotation,
    shapeScale,
  });
  material.setPerspective(camera.fov, window.innerHeight);

  const fire = new THREE.Points(geometry, material);

  // Object metadata
  const uniqueId = `fire_${Date.now()}`;
  fire.name = particle?.id || particle?._id || uniqueId;
  fire.url = uniqueId;
  fire.asset_file_name = 'fire';
  fire.file_name = 'fire';
  fire.mesh_code = 'fire';
  fire.glb_type = 'shapes';
  fire.deleteType = 'particle';
  fire.objType = 'fire';
  fire.createdAt = new Date().toISOString();

  // Store all state variables in userData
  fire.userData = {
    material_settings: {
      shader: 'Standard',
      albedo: color,
      roughness: 0.0,
      metalness: 0.0,
    },
    coreCount: coreCount,
    emberCount: emberCount,
    color: color,
    opacity: opacity,
    size: size,
    range: range,
    turbulence: turbulence,
    life: life,
    sizeVariation: sizeVariation,
    velocity: velocity,
    wind: wind,
    heatIntensity: heatIntensity,
    blending: blending,
    sizeAttenuation: sizeAttenuation,
    shape: shape,
    shapeRotation: shapeRotation,
    shapeScale: shapeScale,
  };

  // Transform controls from particle data
  if (particle?.position) {
    fire.position.set(
      particle.position.x || 0,
      particle.position.y || 0,
      particle.position.z || 0
    );
  }
  if (particle?.rotation) {
    fire.rotation.set(
      particle.rotation.x || 0,
      particle.rotation.y || 0,
      particle.rotation.z || 0
    );
  }
  if (particle?.scale) {
    fire.scale.set(
      particle.scale.x || 1,
      particle.scale.y || 1,
      particle.scale.z || 1
    );
  }

  scene.add(fire);
  resyncSceneParticlePoints(scene);
  // Defer selection logic to next frame to ensure fire is added and selectable
  requestAnimationFrame(() => {
    store.dispatch(setPushIntersectedObject(fire));
    store.dispatch(selected_object(fire));
    selectObjectInScene(fire.name);
  });


  // Animate fire
  const clock = new THREE.Clock();

  const animateFire = () => {
if (!scene.children.includes(fire)) {
  fire.userData.animationId && cancelAnimationFrame(fire.userData.animationId);
  return;
}

    const delta = clock.getDelta();

    // per-particle splitting animation
    const geom = fire.geometry;
    const posAttr = geom.getAttribute('position');
    const positions = posAttr.array;
    const sizes = geom.getAttribute('size')?.array;

    const { velocities: speeds, life: ages, lifespans: lives } = geom.userData;
    const count = sizes ? sizes.length : positions.length / 3;
    const currentConfig = fire.userData || {};

    // emulate the original incremental update frequency (per-frame feel)
    const frameScale =
      Math.min(120, Math.max(30, 1 / Math.max(delta, 0.001))) / 60; // ~1 at 60fps

    const geomSpeed = geom.userData?.config?.speedMultiplier ?? 1.0;
    for (let i = 0; i < count; i++) {
      const idx3 = i * 3;

      // Apply velocity (scaled so speeds are small constants like 0.02)
      positions[idx3] += speeds[idx3] * delta * frameScale * 60 * geomSpeed;
      positions[idx3 + 1] +=
        speeds[idx3 + 1] * delta * frameScale * 60 * geomSpeed; // upward
      positions[idx3 + 2] +=
        speeds[idx3 + 2] * delta * frameScale * 60 * geomSpeed;

      // swirl (use age for rotational motion)
      const swirl = Math.sin(ages[i] * 10) * 0.005;
      positions[idx3] += swirl;
      positions[idx3 + 2] -= swirl;

      // flicker / jitter
      positions[idx3] += (Math.random() - 0.5) * 0.003;
      positions[idx3 + 2] += (Math.random() - 0.5) * 0.003;

      // advance particle 'age' (used for swirl/flicker)
      ages[i] += delta * 1.0 * geomSpeed;

      // reset particle if too high (use configured height or a fallback)
      const limit = currentConfig.range
        ? Math.max(1.0, currentConfig.range * 3)
        : fireHeight || 1.5;
      if (positions[idx3 + 1] > limit) {
        // reset near center
        positions[idx3] = (Math.random() - 0.5) * 0.15;
        positions[idx3 + 1] = 0;
        positions[idx3 + 2] = (Math.random() - 0.5) * 0.15;

        // new splitting speeds
        speeds[idx3] = (Math.random() - 0.5) * 0.02;
        speeds[idx3 + 1] = 0.02 + Math.random() * 0.02;
        speeds[idx3 + 2] = (Math.random() - 0.5) * 0.02;

        ages[i] = 0;
        if (lives && typeof lives[i] !== 'undefined')
          lives[i] = currentConfig.life || life[i] || 1.0;
      }
    }

    posAttr.needsUpdate = true;

    // subtle drift for the whole system
    fire.position.x += (currentConfig?.wind?.x ?? wind.x) * delta;
    fire.position.z += (currentConfig?.wind?.z ?? wind.z) * delta;

    fire.userData.animationId = requestAnimationFrame(animateFire);
  };

  // Store animation function in userData
  fire.userData.animateFunction = animateFire;
  
  // Start animation if particle is not stopped and apply life timeout
  if (!particle?.isStopped) {
    startAnimation(fire, animateFire, fire.name);
    setLifeTimeout(
      fire,
      typeof life === 'number' ? life : parseFloat(life) || 0
    );
  } else {
    clearLifeTimeout(fire);
  }
};

// Helper function to reset fire particles (moved outside for shared access)
function resetFireParticle(
  i,
  positions,
  velocities,
  colors,
  sizes,
  life,
  lifespans,
  config
) {
  const index = i * 3;
  const range = config.range || 1;

  // Initialize near the center for a splitting effect
  positions[index] = (Math.random() - 0.5) * range * 0.15;
  positions[index + 1] = 0;
  positions[index + 2] = (Math.random() - 0.5) * range * 0.15;

  // Splitting speeds (spread outward + upward)
  // scale by config.speedMultiplier when provided
  const s = config?.speedMultiplier ?? 1.0;
  velocities[index] = (Math.random() - 0.5) * 0.02 * s; // x spread
  velocities[index + 1] = (0.02 + Math.random() * 0.02) * s; // upward
  velocities[index + 2] = (Math.random() - 0.5) * 0.02 * s; // z spread

  const color = new THREE.Color();
  color.setHSL(Math.random() * 0.1 + 0.05, 1, Math.random() * 0.3 + 0.5);
  colors[index] = color.r;
  colors[index + 1] = color.g;
  colors[index + 2] = color.b;

  const sizeVariation = config.sizeVariation || 0.3;
  // use configured size if provided, otherwise use the smaller default (0.12)
  const baseSize = typeof config.size !== 'undefined' ? config.size : 0.12;
  sizes[i] = baseSize * (Math.random() * sizeVariation + 0.3);
  // life used as age for swirl; start with small random offset
  life[i] = Math.random() * 1.0;
  // lifespan controls potential future use (not required for splitting but keep)
  lifespans[i] = config.life || 1.0;
}

const updateFire = (scene, fireSystem, newParticleData) => {
  if (!scene || !fireSystem || fireSystem.objType !== 'fire') {
    console.warn('Invalid fire system or scene provided for update');
    return;
  }

  // Get current configuration
  const currentConfig = fireSystem.userData || {};

  // Handle animation state changes
  if (
    typeof newParticleData?.isPlaying !== 'undefined' ||
    typeof newParticleData?.isStopped !== 'undefined'
  ) {
    const isPlaying =
      newParticleData?.isPlaying ?? currentConfig.isPlaying ?? true;
    const isStopped =
      newParticleData?.isStopped ?? currentConfig.isStopped ?? false;

    if (isStopped) {
      stopAnimation(fireSystem, fireSystem.name);
      currentConfig.isStopped = true;
      currentConfig.isPlaying = false;
    } else if (isPlaying) {
      const animateFunction = fireSystem.userData.animateFunction;
      if (animateFunction) {
        startAnimation(fireSystem, animateFunction, fireSystem.name);
        currentConfig.isStopped = false;
        currentConfig.isPlaying = true;
      }
    } else {
      pauseAnimation(fireSystem, fireSystem.name);
      currentConfig.isPlaying = false;
    }
  }

  // Merge current config with new particle data
  const updatedConfig = {
    coreCount: newParticleData?.coreCount ?? currentConfig.coreCount ?? 100,
    emberCount: newParticleData?.emberCount ?? currentConfig.emberCount ?? 150,
    color: newParticleData?.color ?? currentConfig.color ?? '#ffb600',
    opacity: newParticleData?.opacity ?? currentConfig.opacity ?? 0.9,
    // default to a smaller size for better visuals out-of-the-box
    size: newParticleData?.size ?? currentConfig.size ?? 0.12,
    range: newParticleData?.range ?? currentConfig.range ?? 0.5,
    turbulence: newParticleData?.turbulence ?? currentConfig.turbulence ?? 0.1,
    life: newParticleData?.life ?? currentConfig.life ?? 1.0,
    sizeVariation:
      newParticleData?.sizeVariation ?? currentConfig.sizeVariation ?? 0.3,
    velocity: newParticleData?.velocity ??
      currentConfig.velocity ?? { x: 0, y: 0, z: 0 },
    wind: newParticleData?.wind ?? currentConfig.wind ?? { x: 0, y: 0, z: 0 },
    heatIntensity:
      newParticleData?.heatIntensity ?? currentConfig.heatIntensity ?? 0.1,
    blending:
      newParticleData?.blending ?? currentConfig.blending ?? 'NormalBlending',
    sizeAttenuation:
      newParticleData?.sizeAttenuation !== undefined
        ? newParticleData.sizeAttenuation
        : currentConfig.sizeAttenuation !== undefined
          ? currentConfig.sizeAttenuation
          : true,
    shape:
      newParticleData?.shape ??
      currentConfig.shape ??
      getDefaultParticleShape('fire'),
    shapeRotation:
      newParticleData?.shapeRotation ?? currentConfig.shapeRotation ?? 0,
    shapeScale: newParticleData?.shapeScale ?? currentConfig.shapeScale ?? 1.0,
    // control animation speed (1.0 = normal). Increase for faster particle animation.
    speedMultiplier:
      newParticleData?.speedMultiplier ?? currentConfig.speedMultiplier ?? 2.5,
  };
// Handle in-place geometry update for coreCount changes to preserve selection and focus
const isCoreCountProvided = newParticleData?.coreCount !== undefined;
const targetCoreCount = isCoreCountProvided
  ? newParticleData.coreCount === '' || newParticleData.coreCount === null
    ? 0
    : Math.max(0, parseInt(newParticleData.coreCount) || 0)
  : currentConfig.coreCount;

const coreCountChanged =
  isCoreCountProvided && targetCoreCount !== currentConfig.coreCount;

if (coreCountChanged) {
  if (fireSystem.geometry) {
    fireSystem.geometry.dispose();
  }
  const fireHeight =
    (newParticleData?.range ?? currentConfig.range ?? 0.5) * 3;
  const newGeometry = createFireGeometry(
    newParticleData?.range ?? currentConfig.range ?? 0.5,
    fireHeight,
    targetCoreCount,
    newParticleData?.speedMultiplier ?? currentConfig.speedMultiplier ?? 2.5
  );
  newGeometry.computeBoundingBox();
  newGeometry.computeBoundingSphere();
  fireSystem.geometry = newGeometry;
  fireSystem.userData.coreCount = targetCoreCount;
  currentConfig.coreCount = targetCoreCount;
  refreshParticleSelectionOutline();
}

// Check if we need to recreate the fire system (range or color changed significantly)
  // Check if we need to recreate the fire system (particle counts, range, or color changed significantly)
  const needsRecreation =
 
    (newParticleData?.range !== undefined &&
      Math.abs((newParticleData.range ?? 0.5) - (currentConfig.range ?? 0.5)) >
        0.1) 

  if (needsRecreation) {
    // Store animation state
    const wasPlaying = !currentConfig.isStopped;

    // Stop current animation
    if (fireSystem.userData.animationId) {
      cancelAnimationFrame(fireSystem.userData.animationId);
      fireSystem.userData.animationId = null;
    }

    // Store position, rotation, scale
    const position = { ...fireSystem.position };
    const rotation = { ...fireSystem.rotation };
    const scale = { ...fireSystem.scale };
    const name = fireSystem.name;
    const url = fireSystem.url;
    const wasSelected =
      store.getState().assets_object?.select_object === fireSystem ||
      store.getState().assets_object?.select_object?.name === fireSystem.name;

    // Drop outline proxy before removing the old Points object.
    clearOutlineProxyForPoints(fireSystem);
    store.dispatch(removeIntersectedObject(fireSystem));

    // Remove old fire system from scene
    scene.remove(fireSystem);

    // Dispose of old geometry and material
    if (fireSystem.geometry) {
      fireSystem.geometry.dispose();
    }
    if (fireSystem.material) {
      if (fireSystem.material.map) {
        fireSystem.material.map.dispose();
      }
      fireSystem.material.dispose();
    }

    // Create new fire system with updated parameters
    const fireHeight = updatedConfig.range * 3;
    const geometry = createFireGeometry(
      updatedConfig.range,
      fireHeight,
      updatedConfig.coreCount,
      updatedConfig.speedMultiplier
    );
    const material = createFireMaterial({
      color: updatedConfig.color,
      size: updatedConfig.size,
      opacity: updatedConfig.opacity,
      sizeAttenuation: updatedConfig.sizeAttenuation,
      blending: updatedConfig.blending,
      shape: updatedConfig.shape,
      shapeRotation: updatedConfig.shapeRotation,
      shapeScale: updatedConfig.shapeScale,
    });
    material.setPerspective(camera.fov, window.innerHeight);

    const newFire = new THREE.Points(geometry, material);

    // Restore metadata and transforms
    newFire.name = name;
    newFire.url = url;
    newFire.asset_file_name = 'fire';
    newFire.file_name = 'fire';
    newFire.mesh_code = 'fire';
    newFire.glb_type = 'shapes';
    newFire.deleteType = 'particle';
    newFire.objType = 'fire';
    newFire.createdAt = fireSystem.createdAt || new Date().toISOString();

    newFire.position.copy(position);
    newFire.rotation.copy(rotation);
    newFire.scale.copy(scale);

    // Store all state variables in userData
    newFire.userData = {
      material_settings: {
        shader: 'Standard',
        albedo: updatedConfig.color,
        roughness: 0.0,
        metalness: 0.0,
      },
      ...updatedConfig,
      isPlaying: wasPlaying,
      isStopped: !wasPlaying,
    };

    // Create animation function
    const clock = new THREE.Clock();
    const animateFire = () => {
      if (!newFire.parent) return;

      const delta = clock.getDelta();
      const currentUserData = newFire.userData;
      const currentTurbulence =
        currentUserData?.turbulence ?? updatedConfig.turbulence;
      const currentWind = currentUserData?.wind ?? updatedConfig.wind;
      const currentLife = currentUserData?.life ?? updatedConfig.life;

      // per-particle splitting animation similar to the creation path
      const geom = newFire.geometry;
      const posAttr = geom.getAttribute('position');
      const sizeAttr = geom.getAttribute('size');
      const positions = posAttr.array;
      const sizes = sizeAttr
        ? sizeAttr.array
        : new Float32Array(positions.length / 3);

      const {
        velocities: speeds,
        life: ages,
        lifespans: lives,
      } = geom.userData;
      const count = sizes.length;

      const frameScale =
        Math.min(120, Math.max(30, 1 / Math.max(delta, 0.001))) / 60;

      const geomSpeed = geom.userData?.config?.speedMultiplier ?? 1.0;
      for (let i = 0; i < count; i++) {
        const idx3 = i * 3;
        positions[idx3] += speeds[idx3] * delta * frameScale * 60 * geomSpeed;
        positions[idx3 + 1] +=
          speeds[idx3 + 1] * delta * frameScale * 60 * geomSpeed;
        positions[idx3 + 2] +=
          speeds[idx3 + 2] * delta * frameScale * 60 * geomSpeed;

        const swirl = Math.sin(ages[i] * 10) * 0.005;
        positions[idx3] += swirl;
        positions[idx3 + 2] -= swirl;

        positions[idx3] += (Math.random() - 0.5) * 0.003;
        positions[idx3 + 2] += (Math.random() - 0.5) * 0.003;

        ages[i] += delta * 1.0 * geomSpeed;

        const limit = newFire.userData?.range
          ? Math.max(1.0, newFire.userData.range * 3)
          : fireHeight || 1.5;
        if (positions[idx3 + 1] > limit) {
          positions[idx3] = (Math.random() - 0.5) * 0.15;
          positions[idx3 + 1] = 0;
          positions[idx3 + 2] = (Math.random() - 0.5) * 0.15;

          speeds[idx3] = (Math.random() - 0.5) * 0.02;
          speeds[idx3 + 1] = 0.02 + Math.random() * 0.02;
          speeds[idx3 + 2] = (Math.random() - 0.5) * 0.02;

          ages[i] = 0;
          if (lives && typeof lives[i] !== 'undefined')
            lives[i] = currentUserData?.life ?? updatedConfig.life ?? 1.0;
        }
      }

      posAttr.needsUpdate = true;
      if (sizeAttr) sizeAttr.needsUpdate = true;

      newFire.position.x += (currentWind.x || 0) * delta;
      newFire.position.z += (currentWind.z || 0) * delta;

      newFire.userData.animationId = requestAnimationFrame(animateFire);
    };

    newFire.userData.animateFunction = animateFire;

    // Add to scene
    scene.add(newFire);

    // Start animation if it was playing
    if (wasPlaying) {
      startAnimation(newFire, animateFire, newFire.name);
      setLifeTimeout(
        newFire,
        typeof updatedConfig.life === 'number'
          ? updatedConfig.life
          : parseFloat(updatedConfig.life) || 0
      );
    }

    // Update store and selections — keep outline on the live Points object.
    store.dispatch(setPushIntersectedObject(newFire));
    if (wasSelected) {
      store.dispatch(selected_object(newFire));
      selectObjectInScene(newFire.name);
    }
    refreshParticleSelectionOutline();
    resyncSceneParticlePoints(scene);

    return newFire;
  }

  // For non-recreation updates, update the material properties
  if (fireSystem.material) {
    // Update color if changed
    if (newParticleData?.color !== undefined) {
      const shapeKey = String(
        newParticleData?.shape ??
          fireSystem.material.userData?.shape ??
          updatedConfig.shape ??
          'fire'
      ).toLowerCase();
      // Fire PNG keeps its own colors; other shapes use UI tint
      if (shapeKey === 'fire') {
        fireSystem.material.color.set('#ffffff');
      } else {
        const newColor = parseInt(updatedConfig.color.replace('#', '0x'));
        fireSystem.material.color.setHex(newColor);
      }
    }

    // Update opacity if changed
    if (newParticleData?.opacity !== undefined) {
      fireSystem.material.opacity = updatedConfig.opacity;
      fireSystem.material.transparent = updatedConfig.opacity < 1.0;
    }

    // Update size if changed
    if (newParticleData?.size !== undefined) {
      fireSystem.material.size = updatedConfig.size;
      fireSystem.material.userData.baseSize = updatedConfig.size; // ← add this line if missing
    }

    fireSystem.material.needsUpdate = true;
  }

  // Handle shape / texture updates without full recreation
  if (
    fireSystem.material &&
    (newParticleData?.shape ||
      newParticleData?.color !== undefined ||
      newParticleData?.shapeRotation !== undefined ||
      newParticleData?.shapeScale !== undefined)
  ) {
    const newShape =
      newParticleData?.shape ??
      fireSystem.userData?.shape ??
      updatedConfig.shape;
    const newRotation =
      newParticleData?.shapeRotation ??
      fireSystem.userData?.shapeRotation ??
      updatedConfig.shapeRotation;
    const newScale =
      newParticleData?.shapeScale ??
      fireSystem.userData?.shapeScale ??
      updatedConfig.shapeScale;

    // Only update if different from existing
    const oldShape = fireSystem.material.userData?.shape;
    const oldRotation = fireSystem.material.userData?.shapeRotation;
    const oldScale = fireSystem.material.userData?.shapeScale;

    const shapeChanged =
      newShape !== oldShape ||
      newRotation !== oldRotation ||
      newScale !== oldScale ||
      newParticleData?.color !== undefined;

    if (shapeChanged) {
      try {
        const textureSize =
          String(newShape).toLowerCase() === 'fire' ? 128 : 64;
        applyParticleShapeMap(
          fireSystem.material,
          newShape,
          updatedConfig.color,
          newRotation,
          newScale,
          textureSize
        );
        fireSystem.material.userData = fireSystem.material.userData || {};
        fireSystem.material.userData.shape = newShape;
        fireSystem.material.userData.shapeRotation = newRotation;
        fireSystem.material.userData.shapeScale = newScale;
        if (String(newShape).toLowerCase() === 'fire') {
          fireSystem.material.color.set('#ffffff');
          fireSystem.material.vertexColors = false;
          fireSystem.material.blending = THREE.AdditiveBlending;
        }
        fireSystem.material.needsUpdate = true;
      } catch (err) {
        console.warn('Failed to update particle shape texture', err);
      }
    }
  }

  // Update transforms if provided
  if (newParticleData?.position) {
    fireSystem.position.set(
      newParticleData.position.x ?? fireSystem.position.x,
      newParticleData.position.y ?? fireSystem.position.y,
      newParticleData.position.z ?? fireSystem.position.z
    );
  }

  if (newParticleData?.rotation) {
    fireSystem.rotation.set(
      newParticleData.rotation.x ?? fireSystem.rotation.x,
      newParticleData.rotation.y ?? fireSystem.rotation.y,
      newParticleData.rotation.z ?? fireSystem.rotation.z
    );
  }

  if (newParticleData?.scale) {
    fireSystem.scale.set(
      newParticleData.scale.x ?? fireSystem.scale.x,
      newParticleData.scale.y ?? fireSystem.scale.y,
      newParticleData.scale.z ?? fireSystem.scale.z
    );
  }

  // Update userData with new configuration
  fireSystem.userData = {
    ...fireSystem.userData,
    ...updatedConfig,
    material_settings: {
      ...fireSystem.userData.material_settings,
      albedo: updatedConfig.color,
    },
  };

  // Update system metadata if ID changed
  if (newParticleData?.id && newParticleData.id !== fireSystem.name) {
    fireSystem.name = newParticleData.id;
    fireSystem.url = newParticleData.id;
  }
// Update selections and transform controls (only if not already selected to avoid closing open UI popups)
const currentSelected = store.getState().assets_object?.select_object;
if (currentSelected !== fireSystem && currentSelected?.name !== fireSystem.name) {
  store.dispatch(setPushIntersectedObject(fireSystem));
  store.dispatch(selected_object(fireSystem));
  selectObjectInScene(fireSystem.name);
}


  // Manage life timeout: clear any previous timeout then set a new one if requested
  try {
    clearLifeTimeout(fireSystem);
    const lifeVal = updatedConfig?.life;
    const lifeSeconds =
      typeof lifeVal === 'number' ? lifeVal : parseFloat(lifeVal);
    if (!isNaN(lifeSeconds) && lifeSeconds > 0) {
      setLifeTimeout(fireSystem, lifeSeconds);
      // ensure object is visible when a life is set
      fireSystem.visible = true;
      if (fireSystem.userData) {
        fireSystem.userData.isStopped = false;
        fireSystem.userData.isPlaying = true;
      }
    }
  } catch (e) {
    // ignore
  }

  resyncSceneParticlePoints(scene);
  return fireSystem;
};

// Helper function to find fire system by name/id in scene
const findFireSystem = (scene, nameOrId) => {
  return scene.children.find(
    (child) =>
      (child.name === nameOrId || child.url === nameOrId) &&
      child.objType === 'fire'
  );
};

// Convenience function to update fire by name
const updateFireByName = (scene, nameOrId, newParticleData) => {
  const fireSystem = findFireSystem(scene, nameOrId);
  if (fireSystem) {
    return updateFire(scene, fireSystem, newParticleData);
  } else {
    console.warn(`Fire system with name/id "${nameOrId}" not found in scene`);
    return null;
  }
};

export {
  handleTerrain,
  handleWater,
  handleClouds,
  handleStars,
  handleRain,
  handleSnow,
  handleFire,
  handleFoliage,
  updateFire,
  updateFireByName,
};
